import React, { useState, useEffect } from 'react';
import { Home, ClipboardList, Clock, Bell, User } from 'lucide-react';

/**
 * BottomTabBar — Barra de navegação inferior
 * Fundo: #1E293B · Activo: branco · Inactivo: #94A3B8
 * Timer: botão circular verde elevado, mais saliente
 * Usa visualViewport para não ser empurrado pelo teclado virtual
 */
export default function BottomTabBar({
  activeTab,
  onChange,
  alertasCount = 0,
  emProducao = false,
}) {
  const [timerPulse, setTimerPulse] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    if (emProducao) {
      const interval = setInterval(() => setTimerPulse(p => !p), 1200);
      return () => clearInterval(interval);
    }
  }, [emProducao]);

  // Detectar teclado virtual via visualViewport API
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const onResize = () => {
      // Se a viewport visual é significativamente menor que a janela, o teclado está aberto
      const threshold = window.innerHeight * 0.75;
      setKeyboardOpen(vv.height < threshold);
    };

    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
    };
  }, []);

  const tabs = [
    { id: 'inicio', Icon: Home, label: 'Inicio' },
    { id: 'operacoes', Icon: ClipboardList, label: 'Operacoes' },
    { id: 'timer', Icon: Clock, label: null },
    { id: 'alertas', Icon: Bell, label: 'Alertas', badge: alertasCount },
    { id: 'perfil', Icon: User, label: 'Perfil' },
  ];

  // Esconder barra quando o teclado virtual está aberto
  if (keyboardOpen) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] flex items-end justify-around px-2"
      style={{
        background: '#1E293B',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.3)',
        paddingBottom: 'max(env(safe-area-inset-bottom, 8px), 8px)',
        paddingTop: '6px',
        height: '76px',
      }}
      data-testid="bottom-tab-bar"
    >
      {tabs.map(({ id, Icon, label, badge }) => {
        const isActive = activeTab === id;
        const isTimer = id === 'timer';

        if (isTimer) {
          return (
            <div key={id} className="flex flex-col items-center" style={{ marginTop: '-44px' }}>
              <button
                onClick={() => onChange(id)}
                className="relative flex items-center justify-center rounded-full transition-all duration-300"
                style={{
                  width: '72px',
                  height: '72px',
                  background: emProducao ? '#10B981' : '#0B3B30',
                  boxShadow: emProducao
                    ? '0 8px 28px rgba(16,185,129,0.5), 0 0 0 6px rgba(16,185,129,0.15)'
                    : '0 8px 24px rgba(11,59,48,0.5), 0 0 0 6px rgba(11,59,48,0.1)',
                  transform: timerPulse && emProducao ? 'scale(1.08)' : 'scale(1)',
                  transition: 'transform 1.2s ease-in-out, background 0.3s, box-shadow 0.3s',
                }}
                data-testid="tab-timer"
              >
                {emProducao && (
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: 'rgba(16,185,129,0.25)',
                      transform: timerPulse ? 'scale(1.4)' : 'scale(1.15)',
                      transition: 'transform 1.2s ease-in-out',
                    }}
                  />
                )}
                <Clock
                  className="relative z-10"
                  style={{ width: '34px', height: '34px', color: '#FFFFFF' }}
                />
              </button>
            </div>
          );
        }

        return (
          <button
            key={id}
            onClick={() => onChange(id)}
            className="flex flex-col items-center gap-0.5 relative transition-all duration-200"
            style={{ minWidth: '48px' }}
            data-testid={`tab-${id}`}
          >
            <div className="relative flex items-center justify-center">
              <Icon
                style={{
                  width: '28px', height: '28px',
                  color: isActive ? '#FFFFFF' : '#94A3B8',
                  transition: 'color 0.2s',
                }}
              />
              {badge > 0 && (
                <span
                  className="absolute -top-1.5 -right-2.5 flex items-center justify-center rounded-full text-white font-extrabold"
                  style={{
                    minWidth: '18px', height: '18px',
                    fontSize: '10px', padding: '0 4px',
                    background: '#EF4444',
                  }}
                >
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </div>
            {label && (
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? '#FFFFFF' : '#94A3B8',
                  transition: 'color 0.2s',
                  lineHeight: 1,
                }}
              >
                {label}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
